import { CheckCircle2 } from "lucide-react";
import { useCountUp, useInView } from "./Sections";

const BENEFITS = [
  { num: "01", title: "Global Reach", desc: "Direct connections to 220+ countries and 29,000+ Indian pincodes" },
  { num: "02", title: "Fast Delivery", desc: "Optimised air and surface routing for the shortest transit times" },
  { num: "03", title: "Transparent Service", desc: "Zero hidden surcharges, itemised GST billing & clear quotes" },
  { num: "04", title: "Secure Handling", desc: "Careful packaging standards and shipment protection options" },
  { num: "05", title: "Real-Time Tracking", desc: "Scan-level milestones from booking to delivery proof" },
  { num: "06", title: "Personal Support", desc: "Dedicated human support on call and WhatsApp" },
];

function Counter({ value, label, start, testid }) {
  const v = useCountUp(value, start);
  const display = value >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v.toLocaleString("en-IN");
  return (
    <div data-testid={testid} className="border-l border-stone-300 pl-5 first:border-l-0 first:pl-0 sm:pl-8 sm:first:pl-0">
      <p className="font-heading text-4xl font-light tracking-tight text-navy sm:text-5xl">
        {display}<span className="text-saffron">+</span>
      </p>
      <p className="mt-2 text-xs font-semibold text-slate-500">{label}</p>
    </div>
  );
}

export default function WhyIndiaFast({ stats }) {
  const [ref, inView] = useInView();
  return (
    <section className="bg-[#F4F3F1] py-20 sm:py-28" data-testid="why-indiafast" ref={ref}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="flex items-center gap-2 text-xs font-semibold text-slate-500">
          <span className="h-2 w-2 rounded-full bg-saffron" /> Why IndiaFast
        </p>
        <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-navy sm:text-5xl">
          Why IndiaFast?
        </h2>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <div key={b.num} data-testid={`why-card-${i}`}
                 className={`group rounded-2xl border border-stone-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl hover:shadow-stone-200 ${inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}
                 style={{ transitionDelay: `${i * 90}ms` }}>
              <span className="font-heading text-4xl font-light text-stone-300 transition-colors group-hover:text-saffron/50">{b.num}</span>
              <h3 className="mt-3 font-heading text-lg font-semibold text-navy">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{b.desc}</p>
              <CheckCircle2 className="mt-4 h-5 w-5 text-saffron" />
            </div>
          ))}
        </div>
        {stats && (
          <div className="mt-16 grid grid-cols-2 gap-8 border-t border-stone-300 pt-12 sm:grid-cols-4" data-testid="why-stats">
            <Counter value={stats.countries_served || 220} label="Countries served" start={inView} testid="stat-countries" />
            <Counter value={stats.pincodes_covered || 29000} label="Pincodes covered" start={inView} testid="stat-pincodes" />
            <Counter value={stats.courier_partners || 17} label="Courier partners" start={inView} testid="stat-partners" />
            <Counter value={stats.customers || 85000} label="Happy customers" start={inView} testid="stat-customers" />
          </div>
        )}
      </div>
    </section>
  );
}
